import { useEffect, useState } from "react";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import ChangeBookingFlowContainer from "./ChangeBookingFlowContainer";
import type { Booking } from "@/types/Booking";

interface ChangeBookingDialogProps {
  booking: Booking | null;
  triggerRefresh?: () => void;
  showBookingDialog: boolean;
  setShowBookingDialog: (open: boolean) => void;
  initialChangeType?: "datetime" | "duration" | "room" | null;
}

const ChangeBookingDialog = ({
  booking,
  showBookingDialog,
  setShowBookingDialog,
  triggerRefresh,
  initialChangeType,
}: ChangeBookingDialogProps) => {
  // Use a key to force provider remount when dialog opens with new booking
  const [dialogKey, setDialogKey] = useState(0);

  useEffect(() => {
    // Increment key whenever dialog opens to force fresh provider state
    if (showBookingDialog) {
      setDialogKey((prev) => prev + 1);
    }
  }, [showBookingDialog]);

  const handleClose = () => {
    setShowBookingDialog(false);
  };

  return (
    <>
      <Drawer open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DrawerContent>
          {/* DrawerTitle + DrawerDescription are required by Radix for accessibility but
              the inner step screens render their own visible headings (e.g. "Change Room",
              "Select a New Date or Time"), so we hide these visually with `sr-only` to
              reclaim screen space while keeping the announcement intact. */}
          <DrawerHeader className="sr-only">
            <DrawerTitle>Change a Booking</DrawerTitle>
            <DrawerDescription>
              Make changes to your existing booking by following the steps below.
            </DrawerDescription>
          </DrawerHeader>
          <DrawerBody>
            <ChangeBookingFlowContainer
              key={dialogKey}
              booking={booking}
              triggerRefresh={triggerRefresh}
              onClose={handleClose}
              initialChangeType={initialChangeType}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ChangeBookingDialog;
